import { useState, useRef, useCallback, useEffect } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Eye, ImageIcon, Table2, Calculator, Bold, Italic, Underline, 
  Strikethrough, List, ListOrdered, Subscript, Superscript, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Link2, Undo, Redo,
  Palette, Highlighter, Type, Heading1, Heading2, Heading3,
  Quote, Code, Minus, CornerDownLeft, Indent, Outdent, 
  RemoveFormatting, Copy, Clipboard, CheckSquare
} from 'lucide-react';

// Rich Text Editor Component using contentEditable with enhanced toolbar
export function RichTextEditor({ value, onChange, placeholder, apiUrl, minHeight = 250 }) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showFormulaDialog, setShowFormulaDialog] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formula, setFormula] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [fontSize, setFontSize] = useState('3');
  const [fontFamily, setFontFamily] = useState('');

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle content change
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Execute command
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Insert HTML at cursor
  const insertHtml = (html) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const div = document.createElement('div');
      div.innerHTML = html;
      const frag = document.createDocumentFragment();
      let node, lastNode;
      while ((node = div.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    editorRef.current?.focus();
    handleInput();
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Ukuran file maksimal 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${apiUrl}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      insertHtml(`<img src="${data.url}" alt="uploaded image" style="max-width: 100%; height: auto; margin: 8px 0; border-radius: 4px;" />`);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Gagal mengupload gambar. Silakan coba lagi.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Insert formula
  const insertFormula = () => {
    if (!formula.trim()) return;
    try {
      const rendered = katex.renderToString(formula, { throwOnError: false });
      insertHtml(`<span class="formula-container" style="display: inline-block; padding: 2px 6px; background: #f1f5f9; border-radius: 4px; margin: 0 2px;">${rendered}</span>`);
    } catch {
      alert('Format rumus tidak valid');
    }
    setFormula('');
    setShowFormulaDialog(false);
  };

  // Insert table
  const insertTable = () => {
    let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    for (let i = 0; i < tableRows; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        const cellStyle = 'border: 1px solid #ccc; padding: 8px; min-width: 50px;';
        if (i === 0) {
          tableHtml += `<th style="${cellStyle} background: #f5f5f5; font-weight: bold;">Header ${j + 1}</th>`;
        } else {
          tableHtml += `<td style="${cellStyle}">&nbsp;</td>`;
        }
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table><br/>';
    insertHtml(tableHtml);
    setShowTableDialog(false);
  };

  // Insert link
  const insertLink = () => {
    if (!linkUrl.trim()) return;
    const text = linkText || linkUrl;
    insertHtml(`<a href="${linkUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">${text}</a>`);
    setLinkUrl('');
    setLinkText('');
    setShowLinkDialog(false);
  };

  // Insert horizontal rule
  const insertHR = () => {
    insertHtml('<hr style="border: none; border-top: 1px solid #ccc; margin: 16px 0;" />');
  };

  // Insert blockquote
  const insertBlockquote = () => {
    execCommand('formatBlock', 'blockquote');
  };

  // Insert code block
  const insertCodeBlock = () => {
    insertHtml('<pre style="background: #1e293b; color: #e2e8f0; padding: 12px; border-radius: 6px; font-family: monospace; overflow-x: auto; margin: 8px 0;"><code>// Kode disini</code></pre>');
  };

  // Clear formatting
  const clearFormatting = () => {
    execCommand('removeFormat');
  };

  // Toolbar button component
  const ToolbarButton = ({ onClick, active, children, title, disabled, className = '' }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
        active ? 'bg-slate-300' : ''
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-6 bg-slate-300 mx-1" />;

  // Font sizes
  const fontSizes = [
    { value: '1', label: '8pt' },
    { value: '2', label: '10pt' },
    { value: '3', label: '12pt' },
    { value: '4', label: '14pt' },
    { value: '5', label: '18pt' },
    { value: '6', label: '24pt' },
    { value: '7', label: '36pt' },
  ];

  return (
    <div className="rich-text-editor border border-slate-200 rounded-lg overflow-hidden" data-testid="rich-text-editor">
      {/* Toolbar Row 1 - Formatting */}
      <div className="flex items-center gap-0.5 p-2 bg-slate-100 border-b border-slate-200 flex-wrap">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand('undo')} title="Undo (Ctrl+Z)">
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('redo')} title="Redo (Ctrl+Y)">
          <Redo className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Headings */}
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h1')} title="Heading 1">
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h2')} title="Heading 2">
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h3')} title="Heading 3">
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'p')} title="Paragraph">
          <Type className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Font Size */}
        <select
          value={fontSize}
          onChange={(e) => {
            setFontSize(e.target.value);
            execCommand('fontSize', e.target.value);
          }}
          className="h-7 px-2 text-xs border border-slate-300 rounded bg-white"
          title="Ukuran Font"
        >
          {fontSizes.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        
        <Divider />
        
        {/* Text Format */}
        <ToolbarButton onClick={() => execCommand('bold')} title="Tebal (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Miring (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Garis Bawah (Ctrl+U)">
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('strikeThrough')} title="Coret">
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Script */}
        <ToolbarButton onClick={() => execCommand('subscript')} title="Subscript (H₂O)">
          <Subscript className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('superscript')} title="Superscript (x²)">
          <Superscript className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Colors */}
        <div className="relative flex items-center">
          <input
            type="color"
            onChange={(e) => execCommand('foreColor', e.target.value)}
            className="absolute opacity-0 w-7 h-7 cursor-pointer"
            title="Warna Teks"
          />
          <ToolbarButton title="Warna Teks">
            <Palette className="w-4 h-4" />
          </ToolbarButton>
        </div>
        <div className="relative flex items-center">
          <input
            type="color"
            onChange={(e) => execCommand('hiliteColor', e.target.value)}
            className="absolute opacity-0 w-7 h-7 cursor-pointer"
            title="Warna Highlight"
          />
          <ToolbarButton title="Highlight">
            <Highlighter className="w-4 h-4" />
          </ToolbarButton>
        </div>
        
        <Divider />
        
        {/* Clear Formatting */}
        <ToolbarButton onClick={clearFormatting} title="Hapus Format">
          <RemoveFormatting className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Toolbar Row 2 - Structure & Insert */}
      <div className="flex items-center gap-0.5 p-2 bg-slate-50 border-b border-slate-200 flex-wrap">
        {/* Lists */}
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => insertHtml('<ul style="list-style-type: none; padding-left: 20px;"><li>☐ Checklist item</li></ul>')} title="Checklist">
          <CheckSquare className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Indent */}
        <ToolbarButton onClick={() => execCommand('outdent')} title="Kurangi Indentasi">
          <Outdent className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('indent')} title="Tambah Indentasi">
          <Indent className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Alignment */}
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Rata Kiri">
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Rata Tengah">
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Rata Kanan">
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyFull')} title="Rata Kiri Kanan">
          <AlignJustify className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Quote & Code */}
        <ToolbarButton onClick={insertBlockquote} title="Kutipan">
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={insertCodeBlock} title="Blok Kode">
          <Code className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={insertHR} title="Garis Horizontal">
          <Minus className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => insertHtml('<br/>')} title="Line Break">
          <CornerDownLeft className="w-4 h-4" />
        </ToolbarButton>
        
        <Divider />
        
        {/* Insert */}
        <ToolbarButton onClick={() => setShowLinkDialog(true)} title="Sisipkan Link">
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton 
          onClick={() => fileInputRef.current?.click()} 
          title="Upload Gambar"
          disabled={isUploading}
        >
          {isUploading ? (
            <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <ImageIcon className="w-4 h-4" />
          )}
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <ToolbarButton onClick={() => setShowTableDialog(true)} title="Sisipkan Tabel">
          <Table2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => setShowFormulaDialog(true)} title="Sisipkan Rumus Matematika">
          <Calculator className="w-4 h-4" />
        </ToolbarButton>
        
        <div className="flex-1" />
        
        {/* Preview Toggle */}
        <Button
          type="button"
          variant={showPreview ? "default" : "outline"}
          size="sm"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 h-7"
        >
          <Eye className="w-3 h-3" />
          {showPreview ? 'Edit' : 'Preview'}
        </Button>
      </div>

      {/* Editor or Preview */}
      {showPreview ? (
        <div 
          className="p-4 bg-white prose prose-sm max-w-none"
          style={{ minHeight: `${minHeight}px` }}
          dangerouslySetInnerHTML={{ __html: value || '<p class="text-slate-400">Belum ada konten...</p>' }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          className="p-4 bg-white focus:outline-none prose prose-sm max-w-none"
          style={{ minHeight: `${minHeight}px` }}
          data-placeholder={placeholder || "Ketik soal di sini..."}
          data-testid="rich-text-editor-input"
        />
      )}

      {/* Formula Dialog */}
      <Dialog open={showFormulaDialog} onOpenChange={setShowFormulaDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sisipkan Rumus Matematika</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rumus LaTeX</Label>
              <Input
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder="Contoh: x^2 + y^2 = z^2"
              />
              <div className="text-xs text-slate-500 space-y-1">
                <p><strong>Contoh rumus:</strong></p>
                <p>• Pangkat: x^2, a^{'{n}'}</p>
                <p>• Pecahan: \frac{'{a}'}{'{b}'}</p>
                <p>• Akar: \sqrt{'{x}'}, \sqrt[3]{'{x}'}</p>
                <p>• Simbol: \alpha, \beta, \pi, \sum, \int, \infty</p>
                <p>• Kimia: H_2O, CO_2, Ca^{'{2+}'}</p>
              </div>
            </div>
            {formula && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500 mb-2 block">Preview:</Label>
                <div 
                  className="text-center text-lg"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      try {
                        return katex.renderToString(formula, { throwOnError: false });
                      } catch {
                        return '<span class="text-red-500">Format tidak valid</span>';
                      }
                    })()
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormulaDialog(false)}>Batal</Button>
            <Button onClick={insertFormula} disabled={!formula.trim()}>Sisipkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sisipkan Tabel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jumlah Baris</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={tableRows}
                  onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="space-y-2">
                <Label>Jumlah Kolom</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={tableCols}
                  onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg overflow-x-auto">
              <Label className="text-xs text-slate-500 mb-2 block">Preview:</Label>
              <table className="border-collapse w-full">
                <tbody>
                  {Array.from({ length: Math.min(tableRows, 5) }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: Math.min(tableCols, 5) }).map((_, j) => (
                        <td
                          key={j}
                          className={`border border-slate-300 p-2 text-xs text-center ${
                            i === 0 ? 'bg-slate-100 font-bold' : ''
                          }`}
                        >
                          {i === 0 ? `H${j + 1}` : `${i},${j + 1}`}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {(tableRows > 5 || tableCols > 5) && (
                <p className="text-xs text-slate-400 mt-2 text-center">(Preview terbatas 5x5)</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTableDialog(false)}>Batal</Button>
            <Button onClick={insertTable}>Sisipkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sisipkan Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Teks (opsional)</Label>
              <Input
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Teks yang ditampilkan"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Batal</Button>
            <Button onClick={insertLink} disabled={!linkUrl.trim()}>Sisipkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSS for empty placeholder and styling */}
      <style>{`
        .rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .rich-text-editor table {
          border-collapse: collapse;
        }
        .rich-text-editor table td,
        .rich-text-editor table th {
          border: 1px solid #ccc;
          padding: 8px;
        }
        .rich-text-editor img {
          max-width: 100%;
          height: auto;
        }
        .rich-text-editor blockquote {
          border-left: 4px solid #4472C4;
          padding-left: 16px;
          margin: 16px 0;
          color: #64748b;
          font-style: italic;
        }
        .rich-text-editor pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 12px;
          border-radius: 6px;
          overflow-x: auto;
        }
        .rich-text-editor h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
        .rich-text-editor h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; }
        .rich-text-editor h3 { font-size: 1.17em; font-weight: bold; margin: 0.83em 0; }
      `}</style>
    </div>
  );
}

// Question Preview Component
export function QuestionPreview({ question, options, explanation }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="question-preview">
      <h4 className="font-semibold mb-3 text-slate-700">Preview Soal</h4>
      <div 
        className="prose prose-sm max-w-none mb-4"
        dangerouslySetInnerHTML={{ __html: question || '<p class="text-slate-400 italic">Belum ada teks soal...</p>' }}
      />
      {options && options.length > 0 && (
        <div className="space-y-2 mb-4">
          {options.map((opt, i) => (
            <div 
              key={opt.id || i}
              className={`flex items-start gap-3 p-2 rounded ${
                opt.is_correct ? 'bg-green-50 border border-green-200' : 'bg-white border border-slate-200'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                opt.is_correct ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {String.fromCharCode(65 + i)}
              </span>
              <div 
                className="text-sm flex-1 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: opt.text || `Opsi ${String.fromCharCode(65 + i)}` }}
              />
              {opt.weight !== undefined && opt.weight > 0 && (
                <span className="text-xs text-blue-600 font-medium">Bobot: {opt.weight}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {explanation && (
        <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
          <p className="text-xs font-medium text-blue-600 mb-1">Pembahasan:</p>
          <div 
            className="text-sm prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: explanation }}
          />
        </div>
      )}
    </div>
  );
}

// Mini Rich Text Editor for Options (simpler version with image upload)
export function MiniRichTextEditor({ value, onChange, placeholder, disabled, apiUrl }) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showFormulaDialog, setShowFormulaDialog] = useState(false);
  const [formula, setFormula] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle content change
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Execute command
  const execCommand = (command, cmdValue = null) => {
    document.execCommand(command, false, cmdValue);
    editorRef.current?.focus();
    handleInput();
  };

  // Insert HTML at cursor
  const insertHtml = (html) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const div = document.createElement('div');
      div.innerHTML = html;
      const frag = document.createDocumentFragment();
      let node, lastNode;
      while ((node = div.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    editorRef.current?.focus();
    handleInput();
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Ukuran file maksimal 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadUrl = apiUrl || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${uploadUrl}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      insertHtml(`<img src="${data.url}" alt="option image" style="max-width: 100%; max-height: 150px; height: auto; margin: 4px 0; border-radius: 4px;" />`);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Gagal mengupload gambar. Silakan coba lagi.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Insert formula
  const insertFormula = () => {
    if (!formula.trim()) return;
    try {
      const rendered = katex.renderToString(formula, { throwOnError: false });
      insertHtml(`<span class="formula-container" style="display: inline-block; padding: 1px 4px; background: #f1f5f9; border-radius: 3px;">${rendered}</span>`);
    } catch {
      alert('Format rumus tidak valid');
    }
    setFormula('');
    setShowFormulaDialog(false);
  };

  // Mini toolbar button
  const MiniBtn = ({ onClick, title, children, active, isLoading }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled || isLoading}
      className={`p-1 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-300' : ''} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );

  return (
    <div className={`mini-rich-text-editor border border-slate-200 rounded-lg overflow-hidden bg-white ${disabled ? 'opacity-60' : ''}`}>
      {/* Mini Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 bg-slate-50 border-b border-slate-200">
        <MiniBtn onClick={() => execCommand('bold')} title="Tebal (Ctrl+B)">
          <Bold className="w-3.5 h-3.5" />
        </MiniBtn>
        <MiniBtn onClick={() => execCommand('italic')} title="Miring (Ctrl+I)">
          <Italic className="w-3.5 h-3.5" />
        </MiniBtn>
        <MiniBtn onClick={() => execCommand('underline')} title="Garis Bawah (Ctrl+U)">
          <Underline className="w-3.5 h-3.5" />
        </MiniBtn>
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <MiniBtn onClick={() => execCommand('subscript')} title="Subscript (H₂O)">
          <Subscript className="w-3.5 h-3.5" />
        </MiniBtn>
        <MiniBtn onClick={() => execCommand('superscript')} title="Superscript (x²)">
          <Superscript className="w-3.5 h-3.5" />
        </MiniBtn>
        <div className="w-px h-4 bg-slate-300 mx-1" />
        <MiniBtn onClick={() => setShowFormulaDialog(true)} title="Sisipkan Rumus">
          <Calculator className="w-3.5 h-3.5" />
        </MiniBtn>
        <MiniBtn 
          onClick={() => fileInputRef.current?.click()} 
          title="Upload Gambar"
          isLoading={isUploading}
        >
          {isUploading ? (
            <span className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <ImageIcon className="w-3.5 h-3.5" />
          )}
        </MiniBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onBlur={handleInput}
        className="px-3 py-2 focus:outline-none text-sm"
        style={{ minHeight: '60px' }}
        data-placeholder={placeholder || "Ketik pilihan jawaban..."}
      />

      {/* Formula Dialog */}
      <Dialog open={showFormulaDialog} onOpenChange={setShowFormulaDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sisipkan Rumus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rumus LaTeX</Label>
              <Input
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder="Contoh: x^2, \frac{a}{b}, \sqrt{x}"
              />
              <p className="text-xs text-slate-500">
                Contoh: x^2 (pangkat), H_2O (subscript), \frac{'{1}'}{'{2}'} (pecahan), \sqrt{'{x}'} (akar)
              </p>
            </div>
            {formula && (
              <div className="p-3 bg-slate-50 rounded text-center">
                <div 
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      try {
                        return katex.renderToString(formula, { throwOnError: false });
                      } catch {
                        return '<span class="text-red-500 text-sm">Format tidak valid</span>';
                      }
                    })()
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowFormulaDialog(false)}>Batal</Button>
            <Button size="sm" onClick={insertFormula} disabled={!formula.trim()}>Sisipkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSS for placeholder and images */}
      <style>{`
        .mini-rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .mini-rich-text-editor img {
          max-width: 100%;
          max-height: 150px;
          height: auto;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;
